import { numberFormatAsString } from "commons/ultilities";
import { dateTimeDisplay } from "commons/ultilities/date/dayjs";
import { IWorkOrderPo } from "mobx-stores/_models/work-order-po/work-order-po";
import {
  useCalcFirstFullPaperAmountPageWeight,
  useCalcSecondFullPaperAmountPageWeight,
} from "pages/work-order-po/hooks/use-calc-total-page-weight";
import { useMemo } from "react";
import { ElementToPrint } from "share-components/templates/print/element";
import { TEXT_PRINT_COLOR, TEXT_PRINT_FONT_WEIGHT } from "../const";

export const useFrontPage1Elements = ({
  workOrderPoDetail,
}: {
  workOrderPoDetail: IWorkOrderPo;
}) => {
  const customerDisplay = workOrderPoDetail?.customerDisplay;
  const paperOutputProgress = workOrderPoDetail?.paperOutputProgress;
  const printProgress = workOrderPoDetail?.printProgress;
  const productProcess = workOrderPoDetail?.product?.process;

  const { firstFullPaperAmountPageWeight } =
    useCalcFirstFullPaperAmountPageWeight({
      poItemFirstFullPaperAmount:
        workOrderPoDetail?.poItemFirstFullPaperAmount || 0,
      selectedProduct: workOrderPoDetail?.product || null,
    });

  const { secondFullPaperAmountPageWeight } =
    useCalcSecondFullPaperAmountPageWeight({
      poItemSecondFullPaperAmount:
        workOrderPoDetail?.poItemSecondFullPaperAmount || 0,
      selectedProduct: workOrderPoDetail?.product || null,
    });
  const wopoInfoElements = useMemo<ElementToPrint[]>(() => {
    return [
      {
        name: "dealingName",
        value: customerDisplay?.dealingName || "",
        left: "7.7cm",
        top: "3.2cm",
        width: "6.5cm",
      },
      {
        name: "productCode",
        value: workOrderPoDetail?.purchaseOrderItem?.product?.code || "",
        left: "16.19cm",
        top: "3.2cm",
        width: "4.35cm",
      },
      {
        name: "productName",
        value: workOrderPoDetail?.purchaseOrderItem?.product?.name || "",
        left: "7.09cm",
        top: "3.83cm",
        width: "7.22cm",
        textWrap: "nowrap",
      },
      {
        name: "barCode",
        value: workOrderPoDetail?.purchaseOrderItem?.product?.barcode || "",
        left: "16.14cm",
        top: "3.79cm",
        width: "4.45cm",
        textWrap: "nowrap",
      },
      {
        name: "orderDate",
        value: dateTimeDisplay(workOrderPoDetail?.purchaseOrder?.poDate) || "",
        left: "16.08cm",
        top: "6.21cm",
        width: "4.29cm",
      },
      {
        name: "orderNumber",
        value: workOrderPoDetail?.purchaseOrder?.poNumber || "",
        left: "16.03cm",
        top: "5.58cm",
        width: "4.29cm",
      },
      {
        name: "issuedDate",
        value: dateTimeDisplay(workOrderPoDetail?.date) || "",
        left: "5.05cm",
        top: "5.58cm",
        width: "6cm",
      },
      {
        name: "workOrderNumber",
        value: workOrderPoDetail?.woNumber || "",
        left: "5.1cm",
        top: "6.18cm",
        width: "6cm",
      },
      {
        name: "batchNumber",
        value: workOrderPoDetail?.woBatchNumber || "",
        left: "5.18cm",
        top: "6.86cm",
        width: "6cm",
      },
      {
        name: "deliveryDate",
        value:
          dateTimeDisplay(workOrderPoDetail?.purchaseOrderItem?.deliveryDate) ||
          "",
        left: "16.05cm",
        top: "6.86cm",
        width: "4.29cm",
      },
      {
        name: "orderQuantity",
        value: numberFormatAsString(
          workOrderPoDetail?.purchaseOrderItem?.quantityStandard?.toString() ||
            "",
        ),
        left: "16.08cm",
        top: "7.44cm",
        width: "4.29cm",
      },
      {
        name: "productionAmount",
        value: numberFormatAsString(
          workOrderPoDetail?.productionAmount?.toString() || "",
        ),
        left: "5.18cm",
        top: "7.44cm",
        width: "6.01cm",
      },
    ];
  }, [
    customerDisplay?.dealingName,
    workOrderPoDetail?.purchaseOrderItem?.product?.code,
    workOrderPoDetail?.purchaseOrderItem?.product?.name,
    workOrderPoDetail?.purchaseOrderItem?.product?.barcode,
    workOrderPoDetail?.purchaseOrderItem?.deliveryDate,
    workOrderPoDetail?.purchaseOrderItem?.quantityStandard,
    workOrderPoDetail?.purchaseOrder?.poDate,
    workOrderPoDetail?.purchaseOrder?.poNumber,
    workOrderPoDetail?.date,
    workOrderPoDetail?.woNumber,
    workOrderPoDetail?.woBatchNumber,
    workOrderPoDetail?.productionAmount,
  ]);

  const paperOutputElements = useMemo<ElementToPrint[]>(
    () => [
      {
        name: "paperStandard",
        value: workOrderPoDetail?.paperOutputStandardDisplay.standard || "",
        left: "3.96cm",
        top: "8.81cm",
        width: "16.32cm",
        textWrap: "nowrap",
      },
      {
        name: "paperType",
        value: workOrderPoDetail?.paperOutputStandardDisplay.paperType || "",
        left: "2.75cm",
        top: "9.41cm",
        width: "4.7cm",
        textWrap: "nowrap",
      },
      {
        name: "gsmStandard",
        value: numberFormatAsString(
          workOrderPoDetail?.paperOutputStandardDisplay.gsmStandard?.toString() ||
            "",
        ),
        left: "10.25cm",
        top: "9.38cm",
        width: "2cm",
        textWrap: "nowrap",
      },
      {
        name: "paperBrand",
        value: workOrderPoDetail?.paperOutputStandardDisplay.paperBrand || "",
        left: "14.42cm",
        top: "9.41cm",
        width: "2.25cm",
        textWrap: "nowrap",
      },
      {
        name: "operator",
        value: paperOutputProgress?.operator || "",
        left: "2.92cm",
        top: "10.01cm",
        width: "2.2cm",
        textWrap: "nowrap",
      },
      {
        name: "equipment",
        value: paperOutputProgress?.equipment || "",
        left: "7.9cm",
        top: "10.04cm",
        width: "2.3cm",
        textWrap: "nowrap",
      },
      {
        name: "paperOutputCompletedDate",
        value: dateTimeDisplay(paperOutputProgress?.completedDate) || "",
        left: "12.53cm",
        top: "9.98cm",
        width: "3.02cm",
        textWrap: "nowrap",
      },
      {
        name: "paperOutputInspector",
        value: paperOutputProgress?.inspector || "",
        left: "18.5cm",
        top: "10.01cm",
        width: "2.3cm",
        textWrap: "nowrap",
      },
      {
        name: "firstFullPaperSize",
        value:
          workOrderPoDetail?.poItemFirstFullPaperAmount &&
          workOrderPoDetail?.poItemFirstFullPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.firstFullPaperSize?.toString() || "",
              )
            : "",

        left: "5.1cm",
        top: "10.61cm",
        width: "1.6cm",
        textWrap: "nowrap",
      },
      {
        name: "firstFullPaperLength",
        value:
          workOrderPoDetail?.poItemFirstFullPaperAmount &&
          workOrderPoDetail?.poItemFirstFullPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.firstFullPaperLength?.toString() || "",
              )
            : "",
        left: "7.77cm",
        top: "10.64cm",
        width: "1.6cm",
        textWrap: "nowrap",
      },
      {
        name: "firstFullPaperPrintQuantity",
        value: numberFormatAsString(
          workOrderPoDetail?.poItemFirstFullPaperAmount?.toString() || "",
        ),
        left: "11.3cm",
        top: "10.61cm",
        width: "2cm",
        textWrap: "nowrap",
      },

      {
        name: "secondFullPaperSize",
        value:
          workOrderPoDetail?.poItemSecondFullPaperAmount &&
          workOrderPoDetail?.poItemSecondFullPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.secondFullPaperSize?.toString() || "",
              )
            : "",
        left: "5.16cm",
        top: "11.21cm",
        width: "1.6cm",
        textWrap: "nowrap",
      },
      {
        name: "secondFullPaperLength",
        value:
          workOrderPoDetail?.poItemSecondFullPaperAmount &&
          workOrderPoDetail?.poItemSecondFullPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.secondFullPaperLength?.toString() || "",
              )
            : "",
        left: "7.79cm",
        top: "11.21cm",
        width: "1.6cm",
        textWrap: "nowrap",
      },
      {
        name: "secondFullPaperPrintQuantity",
        value: numberFormatAsString(
          workOrderPoDetail?.poItemSecondFullPaperAmount?.toString() || "",
        ),
        left: "11.3cm",
        top: "11.21cm",
        width: "2cm",
        textWrap: "nowrap",
      },
      {
        name: "printPaperSize",
        value:
          workOrderPoDetail?.poItemFirstPrintPaperAmount &&
          workOrderPoDetail?.poItemFirstPrintPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.printPaperSize?.toString() || "",
              )
            : "",
        left: "5.13cm",
        top: "11.78cm",
        width: "1.8cm",
        textWrap: "nowrap",
      },
      {
        name: "printPaperLength",
        value:
          workOrderPoDetail?.poItemFirstPrintPaperAmount &&
          workOrderPoDetail?.poItemFirstPrintPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.printPaperLength?.toString() || "",
              )
            : "",
        left: "8.02cm",
        top: "11.78cm",
        width: "1.8cm",
        textWrap: "nowrap",
      },
      {
        name: "printPaperSP",
        value:
          workOrderPoDetail?.poItemFirstPrintPaperAmount &&
          workOrderPoDetail?.poItemFirstPrintPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.printPaperSP?.toString() || "",
              )
            : "",
        left: "11.25cm",
        top: "11.83cm",
        width: "2.2cm",
        textWrap: "nowrap",
      },
      {
        name: "poItemFirstPrintPaperAmount",
        value: numberFormatAsString(
          workOrderPoDetail?.poItemFirstPrintPaperAmount?.toString() || "",
        ),
        left: "5.2cm",
        top: "12.44cm",
        width: "1.8cm",
        textWrap: "nowrap",
      },
      {
        name: "secondPrintPaperSize",
        value:
          workOrderPoDetail?.poItemSecondPrintPaperAmount &&
          workOrderPoDetail?.poItemSecondPrintPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.secondPrintPaperSize?.toString() || "",
              )
            : "",
        left: "5.07cm",
        top: "12.98cm",
        width: "1.8cm",
        textWrap: "nowrap",
      },
      {
        name: "secondPrintPaperLength",
        value:
          workOrderPoDetail?.poItemSecondPrintPaperAmount &&
          workOrderPoDetail?.poItemSecondPrintPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.secondPrintPaperLength?.toString() || "",
              )
            : "",
        left: "8.02cm",
        top: "13.01cm",
        width: "1.8cm",
        textWrap: "nowrap",
      },
      {
        name: "secondPrintPaperSP",
        value:
          workOrderPoDetail?.poItemSecondPrintPaperAmount &&
          workOrderPoDetail?.poItemSecondPrintPaperAmount > 0
            ? numberFormatAsString(
                productProcess?.secondPrintPaperSP?.toString() || "",
              )
            : "",
        left: "11.2cm",
        top: "13.06cm",
        width: "2.2cm",
        textWrap: "nowrap",
      },
      {
        name: "poItemSecondPrintPaperAmount",
        value: numberFormatAsString(
          workOrderPoDetail?.poItemSecondPrintPaperAmount?.toString() || "",
        ),
        left: "5.2cm",
        top: "13.64cm",
        width: "1.8cm",
        textWrap: "nowrap",
      },
      {
        name: "newElement56",
        value: numberFormatAsString(
          firstFullPaperAmountPageWeight.toString() || "",
          { emtyWhenZero: true, decimalScale: 2 },
        ),
        top: "10.60cm",
        left: "18.9cm",
        height: "0.79cm",
        width: "2cm",
        textWrap: "nowrap",
      },
      {
        name: "newElement57",
        value: numberFormatAsString(
          secondFullPaperAmountPageWeight.toString() || "",
          { emtyWhenZero: true, decimalScale: 2 },
        ),
        top: "11.19cm",
        left: "18.9cm",
        height: "0.79cm",
        width: "2cm",
        textWrap: "nowrap",
      },
    ],
    [
      firstFullPaperAmountPageWeight,
      paperOutputProgress?.completedDate,
      paperOutputProgress?.equipment,
      paperOutputProgress?.inspector,
      paperOutputProgress?.operator,
      productProcess?.firstFullPaperLength,
      productProcess?.firstFullPaperSize,
      productProcess?.printPaperLength,
      productProcess?.printPaperSP,
      productProcess?.printPaperSize,
      productProcess?.secondFullPaperLength,
      productProcess?.secondFullPaperSize,
      productProcess?.secondPrintPaperLength,
      productProcess?.secondPrintPaperSP,
      productProcess?.secondPrintPaperSize,
      secondFullPaperAmountPageWeight,
      workOrderPoDetail?.paperOutputStandardDisplay.gsmStandard,
      workOrderPoDetail?.paperOutputStandardDisplay.paperBrand,
      workOrderPoDetail?.paperOutputStandardDisplay.paperType,
      workOrderPoDetail?.paperOutputStandardDisplay.standard,
      workOrderPoDetail?.poItemFirstFullPaperAmount,
      workOrderPoDetail?.poItemFirstPrintPaperAmount,
      workOrderPoDetail?.poItemSecondFullPaperAmount,
      workOrderPoDetail?.poItemSecondPrintPaperAmount,
    ],
  );

  const printElements = useMemo<ElementToPrint[]>(
    () => [
      {
        name: "printStandardDisplayStandard",
        value: workOrderPoDetail?.printStandardDisplay.standard || "",
        left: "4.14cm",
        top: "16.19cm",
        width: "16cm",
        textWrap: "nowrap",
      },
      {
        name: "printProgressOperator",
        value: printProgress?.operator || "",
        left: "2.97cm",
        top: "17.44cm",
        width: "2.3cm",
        textWrap: "nowrap",
      },
      {
        name: "printProgressEquipment",
        value: printProgress?.equipment || "",
        left: "7.92cm",
        top: "17.41cm",
        width: "2.51cm",
        textWrap: "nowrap",
      },

      {
        name: "printProgressInspector",
        value: printProgress?.inspector || "",
        left: "12.83cm",
        top: "17.44cm",
        width: "2.88cm",
        textWrap: "nowrap",
      },
      {
        name: "printProgressDate",
        value: dateTimeDisplay(printProgress?.completedDate || ""),
        left: "17.92cm",
        top: "17.47cm",
        width: "2.7cm",
        textWrap: "nowrap",
      },

      {
        name: "frontPrintColor",
        value: numberFormatAsString(
          productProcess?.frontPrintColor?.toString() || "",
        ),
        left: "6.26cm",
        top: "18.01cm",
        width: "1.2cm",
        textWrap: "nowrap",
      },
      {
        name: "frontPrintColorCode",
        value: (productProcess?.frontPrintColorCode || "").trim(),
        left: "3.90cm",
        top: "18.01cm",
        width: "16.48cm",
        textWrap: "wrap",
        lineClamp: "2",
        height: "1.24cm",
        textIndent: "6.75cm",
      },
      {
        name: "frontPrintMatte",
        value: productProcess?.frontPrintMatte || "",
        left: "3.8cm",
        top: "19.24cm",
        width: "4.1cm",
        textWrap: "nowrap",
      },
      {
        name: "frontPrintHandleWay",
        value: productProcess?.frontPrintHandleWay || "",
        left: "10.62cm",
        top: "19.21cm",
        width: "3.6cm",
        textWrap: "nowrap",
      },
      {
        name: "frontPrintOtherHandling",
        value: productProcess?.frontPrintOtherHandling || "",
        left: "16.85cm",
        top: "19.24cm",
        width: "3.6cm",
        textWrap: "nowrap",
      },
      {
        name: "backPrintColor",
        value: productProcess?.backPrintColor || "",
        left: "5.7cm",
        top: "20.46cm",
        width: "1.64cm",
        textWrap: "nowrap",
      },
      {
        name: "backPrintColorCode",
        value: productProcess?.backPrintColorCode?.trim() || "",
        left: "3.80cm",
        top: "20.46cm",
        width: "16.01cm",
        textWrap: "wrap",
        height: "1.24cm",
        textIndent: "6.85cm",
      },
      {
        name: "backPrintMatte",
        value: productProcess?.backPrintMatte || "",
        left: "3.82cm",
        top: "21.66cm",
        width: "2.5cm",
        textWrap: "nowrap",
      },
      {
        name: "backPrintHandleWay",
        value: productProcess?.backPrintHandleWay || "",
        top: "21.63cm",
        left: "9.12cm",
        fontSize: "16px",
        width: "2.4cm",
        textWrap: "nowrap",
      },
      {
        name: "backPrintOtherHandling",
        value: productProcess?.backPrintOtherHandling || "",
        top: "21.63cm",
        left: "14.18cm",
        fontSize: "16px",
        width: "2cm",
        textWrap: "nowrap",
      },
      {
        name: "printNote",
        value: productProcess?.printNote || "",
        left: "3.95cm",
        top: "22.89cm",
        width: "16.35cm",
        textWrap: "nowrap",
      },
    ],
    [
      workOrderPoDetail?.printStandardDisplay.standard,
      printProgress?.operator,
      printProgress?.equipment,
      printProgress?.inspector,
      printProgress?.completedDate,
      productProcess?.frontPrintColor,
      productProcess?.frontPrintColorCode,
      productProcess?.frontPrintMatte,
      productProcess?.frontPrintHandleWay,
      productProcess?.frontPrintOtherHandling,
      productProcess?.backPrintColor,
      productProcess?.backPrintColorCode,
      productProcess?.backPrintMatte,
      productProcess?.backPrintHandleWay,
      productProcess?.backPrintOtherHandling,
      productProcess?.printNote,
    ],
  );

  const statisticsPrintElements = useMemo<ElementToPrint[]>(
    () => [
      {
        name: "printProgressAmount",
        value: numberFormatAsString(printProgress?.amount?.toString() || ""),
        top: "24.23cm",
        left: "6.34cm",
        fontSize: "16px",
        height: "0.79cm",
        width: "1.96cm",
        textWrap: "nowrap",
      },

      {
        name: "printProgressCompletedAmount",
        value: numberFormatAsString(
          printProgress?.completedAmount?.toString() || "",
        ),
        top: "26.82cm",
        left: "6.4cm",
        fontSize: "16px",
        height: "0.79cm",
        width: "2.09cm",
        textWrap: "nowrap",
      },
      {
        name: "newElement53",
        value: numberFormatAsString(
          printProgress?.firstGradeAmount?.toString() || "",
        ),
        top: "24.86cm",
        left: "6.4cm",
        fontSize: "16px",
        height: "0.79cm",
        width: "2.12cm",
        textWrap: "nowrap",
      },
      {
        name: "newElement54",
        value: numberFormatAsString(
          printProgress?.secondGradeAmount?.toString() || "",
        ),
        top: "25.52cm",
        left: "6.4cm",
        fontSize: "16px",
        height: "0.79cm",
        width: "2.12cm",
        textWrap: "nowrap",
      },
      {
        name: "newElement55",
        value: numberFormatAsString(
          printProgress?.rejectedAmount?.toString() || "",
        ),
        top: "26.14cm",
        left: "6.4cm",
        fontSize: "16px",
        height: "0.79cm",
        width: "2.20cm",
        textWrap: "nowrap",
      },
    ],
    [
      printProgress?.amount,
      printProgress?.completedAmount,
      printProgress?.firstGradeAmount,
      printProgress?.rejectedAmount,
      printProgress?.secondGradeAmount,
    ],
  );
  const frontPage1Elements = useMemo<ElementToPrint[]>(() => {
    return [
      ...wopoInfoElements,
      ...paperOutputElements,
      ...printElements,
      ...statisticsPrintElements,
    ].map((element) => ({
      ...element,
      fontWeight: TEXT_PRINT_FONT_WEIGHT,
      textColor: TEXT_PRINT_COLOR,
    }));
  }, [
    paperOutputElements,
    wopoInfoElements,
    printElements,
    statisticsPrintElements,
  ]);

  return { frontPage1Elements };
};
